import {ComponentProps} from "@/props.ts";
import React, {useEffect, useState, useCallback, useMemo, useRef} from "react";
import {useNeurotuneStore} from "@/common/store/neurotune";
import {
    IProtocol,
    IProtocolStep,
    TProtocolId, TProtocolStatus,
    TProtocolStepId,
} from "../../types/protocol";
import {IProtocolStepEvent} from "../../types/IProtocolStepEvent";
import {
    loadProtocol,
    loadProtocolSteps,
    loadProtocolEvents,
    loadProtocolFiles,
    updateProtocolStep,
    updateProtocol,
} from "../../services/protocols";
import {useUploadStatusStore} from "@/protocols/persistence/useUploadStatusStore";
import {useNavigate, useParams} from "react-router";
import {Box, Text, Button, Badge, ActionIcon, Group, TextInput} from "@mantine/core";
import {IconArrowLeft, IconPencil, IconPrinter} from "@tabler/icons-react";
import dayjs from "dayjs";
import classes from "./ProtocolReviewPage.module.css";
import {StepRow} from "./components/StepRow";
import {EventListView} from "@/protocols/common/components/EventListView";
import {TWorkflow} from "@/protocols/common/steps/StepView";
import {useProtocolStepOrdering} from "@/protocols/studies/hooks/useProtocolStepOrdering";
import {checkStepTimingWarnings, TimingWarning} from "@/protocols/studies/review/checkStepTiming";
import {interleaveEvents, InterleaveResult, TimelineItem} from "@/protocols/studies/review/interleaveEvents";
import {deepMerge} from "@/utils/merging.ts";
import {DeepPartial} from "@/utils/types/DeepPartial.ts";
import {StepTitleBar} from "@/protocols/common/steps/views/components/StepTitleBar.tsx";
import {showErrorNotification} from "@/common/notifications";

interface ProtocolReviewPageProps extends ComponentProps {
}

const ProtocolReviewPage: React.FC<
    ProtocolReviewPageProps
> = ({}: ProtocolReviewPageProps) => {
    const navigate = useNavigate();
    const params = useParams();
    const protocolId = params.protocolId as TProtocolId;
    const activeCompany = useNeurotuneStore((state) => state.activeCompany);
    const subjects = useNeurotuneStore((state) => state.subjects);
    const loadSubjects = useNeurotuneStore((state) => state.loadSubjects);
    // Server-side files for the protocol (consumed by ProtocolFileManager
    // inside StopDataCollectionStepView). Without this, the review page
    // renders the StopDataCollection step with an empty file table even
    // though files exist on the server — ProtocolRecorder populates this
    // store on its own, but the review page has its own load flow.
    const setServerFiles = useUploadStatusStore((s) => s.setServerFiles);

    const [protocol, setProtocol] = useState<IProtocol | null>(null);
    const [steps, setSteps] = useState<Array<IProtocolStep>>([]);
    const [events, setEvents] = useState<Array<IProtocolStepEvent>>([]);
    const [loading, setLoading] = useState(true);
    const readOnly = protocol?.status !== TProtocolStatus.COMPLETED;

    // Per-step workflow state - which step is in review_edit mode
    const [editingStepId, setEditingStepId] = useState<TProtocolStepId | null>(null);

    // Per-step pending updates (controlled component pattern) - use STATE to trigger re-renders
    const [pendingUpdates, setPendingUpdates] = useState<Map<TProtocolStepId, DeepPartial<IProtocolStep>>>(new Map());

    // Protocol name edit state
    const [editingName, setEditingName] = useState(false);
    const [pendingName, setPendingName] = useState<string>("");
    const [submittingName, setSubmittingName] = useState(false);

    // Get natural step ordering and group membership from the shared hook
    const {orderedSteps, groupsByStepId} = useProtocolStepOrdering(steps);

    // Compute timing warnings based on natural ordering
    const stepTimingWarnings = useMemo(() => {
        return checkStepTimingWarnings(orderedSteps);
    }, [orderedSteps]);

    // Compute interleaved timeline items (steps + events combined)
    const {timeline: timelineItems, eventWarnings} = useMemo((): InterleaveResult => {
        return interleaveEvents(orderedSteps, events, stepTimingWarnings);
    }, [orderedSteps, events, stepTimingWarnings]);

    // Build events by stepId map for quick lookup
    const eventsByStepId = useMemo(() => {
        const map = new Map<TProtocolStepId, IProtocolStepEvent[]>();
        events.forEach(e => {
            if (e.protocolStepId) {
                const existing = map.get(e.protocolStepId) || [];
                existing.push(e);
                map.set(e.protocolStepId, existing);
            }
        });
        return map;
    }, [events]);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            // Load subjects if needed
            if (subjects.length === 0) {
                await loadSubjects(activeCompany.id);
            }

            // Load protocol, steps, events, and server-side files in parallel.
            // `loadProtocolFiles` populates `useUploadStatusStore.serverFiles`,
            // which `ProtocolFileManager` reads to render the file table inside
            // the StopDataCollection step's review view.
            const [loadedProtocol, loadedSteps, loadedEvents, loadedFiles] = await Promise.all([
                loadProtocol(activeCompany.id, protocolId),
                loadProtocolSteps(activeCompany.id, protocolId),
                loadProtocolEvents(activeCompany.id, protocolId),
                loadProtocolFiles(activeCompany.id, protocolId),
            ]);

            setProtocol(loadedProtocol);
            setSteps(loadedSteps);
            setEvents(loadedEvents);
            setServerFiles(loadedFiles);
        } catch (error) {
            console.error("Failed to load protocol data:", error);
        } finally {
            setLoading(false);
        }
    }, [activeCompany.id, protocolId, subjects.length, loadSubjects, setServerFiles]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Get events for a specific step
    const getStepEvents = (
        stepId: TProtocolStepId,
    ): Array<IProtocolStepEvent> => {
        return eventsByStepId.get(stepId) || [];
    };

    // Check if a step is a group member
    const getParentGroupId = (
        stepId: TProtocolStepId,
    ): TProtocolStepId | null => {
        return groupsByStepId[stepId]?.id || null;
    };

    // Determine workflow for a step
    const getStepWorkflow = (stepId: TProtocolStepId): TWorkflow => {
        if (editingStepId === stepId) {
            return "review_edit";
        }
        return "review";
    };

    // Handle edit button click - set step to review_edit mode
    const handleStepEdit = (step: IProtocolStep) => {
        if (!readOnly) {
            setEditingStepId(step.id);
        }
    };

    // Handle step changes in review_edit mode
    const handleStepChange = useCallback((stepId: TProtocolStepId, updates: DeepPartial<IProtocolStep>) => {
        // Merge updates with existing pending updates and trigger re-render
        setPendingUpdates(prev => {
            const existingUpdates = prev.get(stepId) || {};
            const newMap: Map<TProtocolStepId, DeepPartial<IProtocolStep>> = new Map(prev);
            newMap.set(stepId, deepMerge(existingUpdates, updates));
            return newMap;
        });
    }, []);

    // Handle cancel in review_edit mode
    const handleStepCancel = useCallback((stepId: TProtocolStepId) => {
        setPendingUpdates(prev => {
            const newMap = new Map(prev);
            newMap.delete(stepId);
            return newMap;
        });
        setEditingStepId(null);
    }, []);

    // Handle update in review_edit mode - commit changes
    // Use ref to avoid stale closure while still having access to current steps
    const stepsRef = useRef(steps);
    useEffect(() => {
        stepsRef.current = steps;
    }, [steps]);

    const handleStepUpdate = useCallback(async (stepId: TProtocolStepId) => {
        const stepPendingUpdates = pendingUpdates.get(stepId);
        if (!stepPendingUpdates) {
            setEditingStepId(null);
            return;
        }

        try {
            // Get fresh step reference from ref to avoid stale closure
            const currentStep = stepsRef.current.find(s => s.id === stepId);
            if (!currentStep) {
                setEditingStepId(null);
                return;
            }

            // Build updated step by merging current step with pending updates
            const updatedStep = deepMerge(currentStep, stepPendingUpdates)

            await updateProtocolStep(activeCompany.id, updatedStep).then(() => {
                setPendingUpdates(prev => {
                    const newMap = new Map(prev);
                    newMap.delete(stepId);
                    return newMap;
                });
                // Update the step in state by merging the committed changes.
                // Use functional updater to avoid relying on the closure-captured `steps`.
                setSteps(prev => {
                    const newSteps = [...prev];
                    const idx = prev.findIndex(s => s.id === stepId);
                    if (idx >= 0) newSteps[idx] = updatedStep;
                    return newSteps;
                });
                // Exit edit mode only after a successful commit; failures keep the user in edit mode.
                setEditingStepId(null);
            });
        } catch (error) {
            console.error("Failed to update step:", error);
        }
    }, [activeCompany.id, loadData, pendingUpdates]);

    const handleNameEdit = useCallback(() => {
        if (!protocol) return;
        setEditingName(true);
        setPendingName(protocol.name);
    }, [protocol]);

    const handleNameCancel = useCallback(() => {
        setEditingName(false);
        setPendingName("");
    }, []);

    const handleNameSave = useCallback(async () => {
        const trimmed = pendingName.trim();
        if (!protocol || trimmed.length === 0 || submittingName) return;
        setSubmittingName(true);
        try {
            const updated = await updateProtocol(activeCompany.id, {
                ...protocol,
                name: trimmed,
            });
            setProtocol(updated);
            setEditingName(false);
            setPendingName("");
        } catch (error) {
            console.error("Failed to update protocol name:", error);
            showErrorNotification({
                title: "Update failed",
                message: "Failed to update protocol name.",
            });
        } finally {
            setSubmittingName(false);
        }
    }, [protocol, pendingName, submittingName, activeCompany.id]);

    const handleNameKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Escape") {
            e.preventDefault();
            handleNameCancel();
        } else if (e.key === "Enter") {
            e.preventDefault();
            void handleNameSave();
        }
    }, [handleNameCancel, handleNameSave]);

    // Handle print
    const handlePrint = () => {
        window.print();
    };

    const subjectName =
        subjects.find((s) => s.id === protocol?.subjectId)?.name ||
        protocol?.subjectId ||
        "";

    // Render a step timeline item
    const renderStepItem = (
        step: IProtocolStep,
        timingWarning?: TimingWarning,
    ) => {
        const stepEvents = getStepEvents(step.id);
        const parentGroupId = getParentGroupId(step.id);
        const isGroupMember = parentGroupId !== null;
        const workflow = getStepWorkflow(step.id);

        // Merge step with pending updates if any exist (for controlled component pattern)
        const stepPendingUpdates = pendingUpdates.get(step.id);
        const stepWithPendingUpdates = stepPendingUpdates
            ? deepMerge(step, stepPendingUpdates)
            : step;

        return (
            <Box key={step.id} className={classes.timelineItem}>
                {/* Step content, key is used to force a rerender when a step is changed/updated */}
                <Box key={`step-${step.id}-${workflow}`} className={classes.timelineContent}>
                    <StepRow
                        step={stepWithPendingUpdates}
                        events={stepEvents}
                        isGroupMember={isGroupMember}
                        workflow={workflow}
                        onEdit={handleStepEdit}
                        onChange={handleStepChange}
                        onCancel={handleStepCancel}
                        onUpdate={handleStepUpdate}
                        timingWarning={timingWarning}
                        eventWarnings={eventWarnings}
                        timezone={"UTC"}
                        readOnly={readOnly}
                    />
                </Box>
            </Box>
        );
    };

    // Render grouped orphan events (events without protocolStepId)
    const renderOrphanEventsItem = (
        item: Extract<TimelineItem, { type: "orphan-events" }>,
    ) => {
        // Filter event warnings to only include warnings for events in this group
        const filteredWarnings = new Map(
            [...eventWarnings.entries()].filter(([id]) =>
                item.events.some(e => e.id === id)
            )
        );

        return (
            <Box
                key={`orphan-${item.events[0]?.id}`}
                className={classes.eventItem}
            >
                <EventListView
                    events={item.events}
                    eventWarnings={filteredWarnings}
                    timezone={"UTC"}
                    readonly={true}
                />
            </Box>
        );
    };

    // Render a timeline item (step or orphan-events)
    const renderTimelineItem = (item: TimelineItem) => {
        if (item.type === "step") {
            return renderStepItem(item.step, item.timingWarning);
        } else {
            return renderOrphanEventsItem(item);
        }
    };

    if (loading) {
        return (
            <Box className={classes.container}>
                <Text>Loading protocol...</Text>
            </Box>
        );
    }

    if (!protocol) {
        return (
            <Box className={classes.container}>
                <Text>Protocol not found</Text>
            </Box>
        );
    }

    return (
        <Box className={classes.container}>
            {/* Header */}
            <Box className={classes.header}>
                <Box className={classes.headerLeft}>
                    <ActionIcon
                        variant="subtle"
                        size="lg"
                        onClick={() => navigate("/sessions/review")}
                        className={classes.backButton}
                        data-testid="back-button"
                    >
                        <IconArrowLeft size={18}/>
                    </ActionIcon>
                    {editingName ? (
                        <Group
                            gap="xs"
                            align="center"
                            wrap="nowrap"
                            className={classes.nameEditGroup}
                        >
                            <TextInput
                                value={pendingName}
                                onChange={(e) => setPendingName(e.currentTarget.value)}
                                onKeyDown={handleNameKeyDown}
                                error={pendingName.trim().length === 0 ? "Name is required" : undefined}
                                autoFocus
                                size="md"
                                className={classes.nameInput}
                                data-testid="protocol-name-input"
                            />
                            <Button
                                size="xs"
                                onClick={() => void handleNameSave()}
                                disabled={pendingName.trim().length === 0 || submittingName}
                                loading={submittingName}
                                data-testid="protocol-name-save"
                            >
                                Save
                            </Button>
                            <Button
                                size="xs"
                                variant="default"
                                onClick={handleNameCancel}
                                disabled={submittingName}
                                data-testid="protocol-name-cancel"
                            >
                                Cancel
                            </Button>
                        </Group>
                    ) : (
                        <>
                            <Text className={classes.title}>{protocol.name}</Text>
                            {!readOnly && (
                                <ActionIcon
                                    variant="subtle"
                                    size="md"
                                    onClick={handleNameEdit}
                                    className={classes.editNameButton}
                                    data-testid="protocol-name-edit-button"
                                    aria-label="Edit protocol name"
                                >
                                    <IconPencil size={16} />
                                </ActionIcon>
                            )}
                        </>
                    )}
                </Box>
                <Box className={classes.headerRight}>
                    <Button
                        variant="default"
                        size="sm"
                        leftSection={<IconPrinter size={16}/>}
                        onClick={handlePrint}
                        className={classes.noPrint}
                    >
                        Print
                    </Button>
                </Box>
            </Box>

            {/* Protocol Info Bar */}
            <Box className={classes.infoBar}>
                <Text size="sm">
                    <strong>Subject:</strong> {subjectName}
                </Text>
                <Box size="sm">
                    <strong>Status:</strong> {formatStatus(protocol.status)}
                </Box>
                <Text size="sm">
                    <strong>Created:</strong>{" "}
                    {dayjs(protocol.createdAt).format("MMM D, YYYY h:mm A")}
                </Text>
            </Box>

            {/* Timeline Step List */}
            <Box className={classes.timeline}>
                {timelineItems.length > 0 ? (
                    timelineItems.map(renderTimelineItem)
                ) : (
                    <Text size="sm" c="dimmed" ta="center" py="xl">
                        No steps found in this protocol
                    </Text>
                )}
            </Box>
        </Box>
    );
};


const formatStatus = (status: string) => {
    switch (status) {
        case "in_progress":
            return (
                <Badge color="blue" variant="light">
                    In Progress
                </Badge>
            );
        case "completed":
            return (
                <Badge color="green" variant="light">
                    Completed
                </Badge>
            );
        default:
            return <Badge variant="light">{status}</Badge>;
    }
};

export {ProtocolReviewPage};